/**
 * Service pour gérer les appels API de manière sécurisée
 * Fournit des méthodes utilitaires pour traiter les réponses API et éviter les erreurs courantes
 */

/**
 * Vérifie si une valeur est un tableau
 * @param value La valeur à vérifier
 * @returns true si la valeur est un tableau, false sinon
 */
export const isArray = (value: any): boolean => {
  return Array.isArray(value);
};

/**
 * S'assure qu'une valeur est un tableau
 * Si la valeur est déjà un tableau, la retourne
 * Si la valeur est null ou undefined, retourne un tableau vide
 * Sinon, encapsule la valeur dans un tableau
 * @param value La valeur à convertir en tableau
 * @returns Un tableau
 */
export const ensureArray = <T>(value: any): T[] => {
  if (value === null || value === undefined) {
    return [];
  }
  return isArray(value) ? value : [value];
};

/**
 * Extrait les données d'une réponse API de manière sécurisée
 * S'adapte aux différentes structures de réponse possibles (data.data, data, etc.)
 * @param response La réponse API à traiter
 * @returns Un tableau contenant les données, ou un tableau vide si aucune donnée n'est trouvée
 */
export const extractApiData = <T>(response: any): T[] => {
  if (!response) return [];
  
  // Si la réponse est directement un tableau, on la retourne
  if (isArray(response)) {
    return response;
  }
  
  // Si response.data.data existe et est un tableau, on le retourne
  if (response.data && isArray(response.data.data)) {
    return response.data.data;
  }
  
  // Si response.data existe et est un tableau, on le retourne
  if (response.data && isArray(response.data)) {
    return response.data;
  }
  
  // Si response.data.data existe mais n'est pas un tableau, on l'encapsule
  if (response.data && response.data.data !== undefined) {
    return ensureArray(response.data.data);
  }
  
  // Si response.data existe mais n'est pas un tableau, on l'encapsule
  if (response.data !== undefined) {
    return ensureArray(response.data);
  }
  
  // En dernier recours, retourne un tableau vide
  return [];
};

/**
 * Vérifie si une réponse API est un succès
 * @param response La réponse API à vérifier
 * @returns true si la réponse est un succès, false sinon
 */
export const isApiSuccess = (response: any): boolean => {
  return response && response.data && response.data.success === true;
};

/**
 * Extrait les messages d'erreur d'une réponse API
 * @param response La réponse API à traiter
 * @returns Un tableau de messages d'erreur
 */
export const getApiErrors = (response: any): string[] => {
  if (!response || !response.data) return ["Erreur de connexion au serveur"];
  
  if (isArray(response.data.errors)) {
    return response.data.errors;
  }
  
  if (response.data.message) {
    return [response.data.message];
  }
  
  return ["Une erreur s'est produite"];
};

// Export de l'objet principal du service
export const ApiService = {
  extractApiData,
  ensureArray,
  isArray,
  isApiSuccess,
  getApiErrors
};

export default ApiService;